import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Check, Truck, ChefHat, Leaf, Sprout, BadgeCheck, Drumstick, Sparkles, HeartHandshake, PartyPopper, Cake, Heart, Building2, UsersRound, Music, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Motion";
import { ChooseYourCurry } from "../components/ChooseYourCurry";
import InstagramFeed from "../components/InstagramFeed";
import PageHeader from "../components/layout/PageHeader";
import { cateringTiers } from "../data/menu";
import { business } from "../data/site";
import { api } from "../lib/api";
import SEO from "../components/SEO";

const accentMap = {
  green: { ring: "border-emerald-600/60", text: "text-emerald-400" },
  red: { ring: "border-[#ffd100]", text: "text-[#ffd100]" },
  purple: { ring: "border-purple-600/60", text: "text-purple-400" },
};

const packageOptions = ["Family Celebration ($14.99)", "Festive Gathering ($19.99)", "Grand Celebration ($24.99)", "Tray Orders", "Not sure yet"];

export default function Catering() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", guests: "", package: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/catering/inquiry", form);
      toast.success("Thank you! We received your catering request and will call you soon. A confirmation email is on its way to your inbox.");
      setForm({ name: "", email: "", phone: "", event_date: "", guests: "", package: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us at " + business.phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="catering-page">
      <SEO
        route="catering"
        fallbackTitle="Indian Catering in Ottawa | Shaan Curry House"
        fallbackDescription="Full-service Indian catering for weddings, corporate lunches and events. Halal, vegan and vegetarian menus."
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Catering", url: "/catering" },
        ]}
      />
      {/* 1 — SIMPLE SECTION */}
      <PageHeader
        eyebrow="Delicious Food. Memorable Moments."
        title="Catering Menu"
        subtitle="Perfect for every celebration. Pick a package below, then send us a quick request — or call and we handle the rest."
        center
      >
        <a href="#catering-form" data-testid="catering-hero-request"
          className="inline-flex items-center justify-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold text-lg px-8 py-4 rounded-full hover:bg-[#e5bc00] transition-colors">
          Request a Quote
        </a>
        <a href={`tel:${business.phoneRaw}`} data-testid="catering-hero-call"
          className="inline-flex items-center justify-center gap-2 border-2 border-white/25 text-white font-bold text-lg px-8 py-4 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors">
          <Phone size={20} /> {business.phone}
        </a>
      </PageHeader>

      {/* 2 — OUR STORY / THE SHAAN EXPERIENCE */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20 border-t border-white/10">
        <Reveal className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-[#ffd100] font-semibold uppercase tracking-[0.24em] text-sm mb-3">Our Story — The Shaan Experience</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight">More than just a meal</h2>
          <p className="text-neutral-300 text-lg mt-5 leading-relaxed">
            At Shaan Curry House, food is a celebration of tradition, flavour and togetherness. From our kitchen to your table, we bring authentic Indian cuisine made with passion and the finest ingredients.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="shaan-pillars">
          {[
            { icon: ChefHat, title: "Authentic Recipes", body: "Traditional Indian recipes with rich heritage and taste." },
            { icon: Sparkles, title: "Fresh Ingredients", body: "We use only the freshest ingredients for every dish." },
            { icon: HeartHandshake, title: "Made With Care", body: "Every dish is crafted with passion and perfection." },
            { icon: PartyPopper, title: "Every Occasion", body: "Catering for events, parties, and special gatherings." },
          ].map(({ icon: Icon, title, body }) => (
            <Reveal key={title}>
              <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 h-full hover:border-[#ffd100]/50 transition-colors">
                <div className="w-12 h-12 grid place-items-center rounded-full bg-[#ffd100]/10 mb-4">
                  <Icon size={22} className="text-[#ffd100]" />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-2">{title}</h3>
                <p className="text-neutral-400 text-base leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 3 — EXQUISITE CATERING CTA BANNER */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-20">
        <Reveal>
          <div className="bg-gradient-to-r from-[#ffd100] to-[#ffb400] rounded-3xl p-8 md:p-14 flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="flex-1 text-center md:text-left">
              <p className="text-[#0a0a0a]/70 font-bold uppercase tracking-[0.24em] text-sm mb-2">Exquisite Catering</p>
              <h2 className="font-display text-4xl md:text-5xl font-black text-[#0a0a0a] tracking-tight leading-tight">
                Delicious. Reliable. Memorable.
              </h2>
              <p className="text-[#0a0a0a]/80 text-lg mt-3 max-w-xl">
                Let us take care of your next event with a menu built around your guests, your budget and your date.
              </p>
            </div>
            <a href="#catering-form" data-testid="exquisite-cta"
              className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#ffd100] font-bold text-lg px-8 py-4 rounded-full hover:bg-black/90 transition-colors shrink-0">
              Request a Quote <ArrowRight size={18} />
            </a>
          </div>
        </Reveal>
      </section>

      {/* 4 — CATERING FOR EVERY OCCASION */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-20">
        <Reveal className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-[#ffd100] font-semibold uppercase tracking-[0.24em] text-sm mb-3">Catering For Every Occasion</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight">Every moment, made memorable</h2>
          <p className="text-neutral-300 text-lg mt-4">From small get-togethers to grand celebrations, we make every moment special.</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5" data-testid="occasions-grid">
          {[
            { icon: Cake, title: "Birthdays", body: "Delicious food that makes birthdays more special.", img: "/images/events/birthdays.jpg", span: "md:col-span-3" },
            { icon: Heart, title: "Weddings", body: "Elegant menus for your big-day celebrations.", img: "/images/events/weddings.jpg", span: "md:col-span-3" },
            { icon: Building2, title: "Corporate", body: "Professional catering for meetings and events.", img: "/images/events/corporate.jpg", span: "md:col-span-2" },
            { icon: UsersRound, title: "Private Parties", body: "Perfect for family & friends gatherings.", img: "/images/events/parties.jpg", span: "md:col-span-2" },
            { icon: Music, title: "Festivals", body: "Celebrate festivals with authentic Indian flavours.", img: "/images/events/festivals.jpg", span: "md:col-span-2" },
          ].map(({ icon: Icon, title, body, img, span }) => (
            <Reveal key={title} className={span}>
              <div className="group relative rounded-3xl overflow-hidden h-80 md:h-[26rem] hover:-translate-y-1 transition-all">
                <img src={img} alt={title} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-7 md:p-8">
                  <div className="w-14 h-14 grid place-items-center rounded-full bg-[#ffd100] mb-4">
                    <Icon size={24} className="text-[#0a0a0a]" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">{title}</h3>
                  <p className="text-neutral-200 text-base md:text-lg mt-2 leading-snug max-w-md">{body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 5 — PACKAGES */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
        <Reveal className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-[#ffd100] font-semibold uppercase tracking-[0.24em] text-sm mb-3">Catering Packages</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight">Delicious options for every celebration</h2>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-7 items-start">
          {cateringTiers.map((tier) => {
            const a = accentMap[tier.accent];
            return (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`tier-${tier.accent}`}
                className={`relative bg-[#141414] rounded-2xl border-2 ${tier.highlight ? "border-[#ffd100] lg:-mt-4 lg:mb-4" : a.ring} p-7 md:p-8`}
              >
                {tier.highlight && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#ffd100] text-[#0a0a0a] text-sm font-bold px-4 py-1 rounded-full">MOST POPULAR</span>
                )}
                <h3 className="font-display font-semibold text-2xl md:text-3xl text-white text-center">{tier.name}</h3>
                <div className="text-center mt-4 mb-2">
                  <span className="font-display font-bold text-5xl text-[#ffd100]">{tier.price}</span>
                  <span className="block text-neutral-400 text-sm font-semibold uppercase tracking-wider mt-1">{tier.per}</span>
                </div>
                <p className="text-center text-neutral-400 text-base mb-6 min-h-[3rem]">{tier.for}</p>

                <div className="space-y-5">
                  {tier.sections.map((s) => (
                    <div key={s.label}>
                      <p className={`font-display font-semibold text-lg ${a.text} mb-2`}>{s.label}</p>
                      <ul className="space-y-1.5">
                        {s.items.map((it) => (
                          <li key={it} className="flex items-start gap-2 text-neutral-200 text-base">
                            <Check size={18} className="text-[#ffd100] shrink-0 mt-1" /> {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <a href="#catering-form"
                  className={`mt-7 block text-center font-bold text-lg py-3.5 rounded-full transition-colors ${tier.highlight ? "bg-[#ffd100] text-[#0a0a0a] hover:bg-[#e5bc00]" : "border-2 border-white/20 text-white hover:border-[#ffd100] hover:text-[#ffd100]"}`}>
                  Request This
                </a>
              </motion.div>
            );
          })}
        </div>

        {/* Choose Your Curry — sits between the tier grid and the tray/buffet pills */}
        <Reveal className="mt-10">
          <div className="mb-6" data-testid="catering-customize-note">
            <p className="text-white font-extrabold text-2xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-center md:text-left">
              We&apos;re happy to <span className="text-[#ffd100]">customize</span> the menu and options based on your preferences and requirements.
            </p>
          </div>
          <div
            data-testid="dietary-banner"
            className="bg-[#ffd100] rounded-2xl px-6 md:px-10 py-6 md:py-8 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mb-6 shadow-[0_10px_40px_-15px_rgba(255,209,0,0.5)]"
          >
            {[
              { icon: Leaf, label: "Vegetarian" },
              { icon: Sprout, label: "Vegan" },
              { icon: BadgeCheck, label: "100% Halal" },
              { icon: Drumstick, label: "Non-Veg" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center justify-center gap-3 md:gap-4">
                <Icon size={30} className="text-[#0a0a0a] shrink-0" strokeWidth={2.2} />
                <span className="text-[#0a0a0a] font-black text-lg md:text-xl lg:text-2xl tracking-tight whitespace-nowrap">
                  {label}
                </span>
              </div>
            ))}
          </div>
          <ChooseYourCurry />
        </Reveal>

        {/* compact add-ons note */}
        <Reveal className="mt-8 flex flex-col sm:flex-row gap-4 justify-center text-center">
          <span className="inline-flex items-center gap-2 text-neutral-300 text-base bg-[#141414] border border-white/10 rounded-full px-5 py-3">
            <Truck size={18} className="text-[#ffd100]" /> Tray Orders also available — ask for pricing
          </span>
          <span className="inline-flex items-center gap-2 text-neutral-300 text-base bg-[#141414] border border-white/10 rounded-full px-5 py-3">
            <ChefHat size={18} className="text-[#ffd100]" /> Full Buffet Setup Service (delivery, setup & operator)
          </span>
        </Reveal>
      </section>

      {/* 3 — FORM */}
      <section id="catering-form" className="bg-[#141414] border-t border-white/10 scroll-mt-24">
        <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-20">
          <Reveal className="text-center mb-10">
            <h2 className="font-display font-bold text-4xl md:text-5xl text-white tracking-tight">Request Catering</h2>
            <p className="mt-4 text-neutral-400 text-lg">Fill in a few details and we&apos;ll call you back. Prefer to talk now? Call <a href={`tel:${business.phoneRaw}`} className="text-[#ffd100] font-semibold">{business.phone}</a>.</p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="catering-form" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Your Name *" testid="cat-name" value={form.name} onChange={update("name")} placeholder="Full name" />
                <Field label="Phone *" testid="cat-phone" value={form.phone} onChange={update("phone")} placeholder="(613) 000-0000" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Email *" testid="cat-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" />
                <Field label="Event Date" testid="cat-date" type="date" value={form.event_date} onChange={update("event_date")} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Number of Guests" testid="cat-guests" value={form.guests} onChange={update("guests")} placeholder="e.g. 50" />
                <div>
                  <label className="block text-sm font-semibold text-neutral-300 mb-2 uppercase tracking-wider">Package</label>
                  <select data-testid="cat-package" value={form.package} onChange={update("package")}
                    className="w-full bg-[#0a0a0a] border border-white/20 rounded-xl px-5 py-4 text-white text-lg focus:border-[#ffd100] outline-none">
                    <option value="">Select a package</option>
                    {packageOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-300 mb-2 uppercase tracking-wider">Message</label>
                <textarea data-testid="cat-message" value={form.message} onChange={update("message")} rows={4} placeholder="Tell us about your event..."
                  className="w-full bg-[#0a0a0a] border border-white/20 rounded-xl px-5 py-4 text-white text-lg focus:border-[#ffd100] outline-none placeholder:text-neutral-600" />
              </div>
              <button type="submit" disabled={submitting} data-testid="catering-submit-btn"
                className="w-full bg-[#ffd100] text-[#0a0a0a] font-bold text-lg py-4 rounded-full hover:bg-[#e5bc00] transition-colors disabled:opacity-60">
                {submitting ? "Sending..." : "Send Catering Request"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* 4 — INSTAGRAM FEED */}
      <InstagramFeed
        heading="Fresh from our kitchen"
        subheading="Behind the scenes, catering setups and the latest dishes — follow along and share your visit with #ShaanCurryHouse."
      />
    </div>
  );
}

const Field = ({ label, testid, type = "text", ...rest }) => (
  <div>
    <label className="block text-sm font-semibold text-neutral-300 mb-2 uppercase tracking-wider">{label}</label>
    <input type={type} data-testid={testid} {...rest}
      className="w-full bg-[#0a0a0a] border border-white/20 rounded-xl px-5 py-4 text-white text-lg focus:border-[#ffd100] outline-none placeholder:text-neutral-600" />
  </div>
);
